import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from '../../../styles/automation/executionsteps.module.scss';
import { EXECUTION_MODES, LABEL_MAP, SKIP_ARGUMENTS } from '../constant';
import useVariables from '../ReplayPage/hooks/useVariables';
import { replaceFileRefsWithNames } from '../../../utils/automation/variableUtils';
import {
    resolveDynamicStepIdentity,
    getDynamicBindingsForStep,
    isDynamicStep,
} from '../../AutomationStudio/utils/common';
import {
    parseDynamicIntentLabel,
    resolveDynamicIntentRaw,
    buildUniqueSourceKeyBindingRows,
    uniqueIntentTokensFromBindings,
} from '../../AutomationStudio/utils/stepMetaInfo';
import StepMetaInfoRunInfo from '../../AutomationStudio/components/RightPanel/StepEditorView/MetaInfo/StepMetaInfoRunInfo';

/**
 * `bag.stepId.<uuid>.*` → display step number; unknown uuid → ''; else trimmed placeholder.
 *
 * Deliberate parity duplicate of the Studio helper at
 * `AutomationStudio/components/RightPanel/StepEditorView/StepEditorView.jsx` (~line 79),
 * with one intentional divergence: an added null-guard on `uuidToDisplayStep`. The NLP prop
 * chain defaults `uuidToDisplayStep` to `null` (Studio always passes a real Map), so this
 * helper must tolerate a missing Map without throwing. If you change either side, keep the
 * `bag.stepId.<uuid>` matching semantics aligned.
 *
 * We intentionally don't promote this to a shared util — the plan
 * (`docs/plans/2026-05-25-nlp-automation-dynamic-chip-design.md`, "Cross-feature import policy")
 * defers that to a future refactor that also moves the imported helpers.
 */
function bindingPlaceholderDisplay(placeholder, uuidToDisplayStep) {
    const s = String(placeholder ?? '').trim();
    if (!s) return '';
    const m = s.match(/bag\.stepId\.([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (!m) return s;
    const uuid = m[1].toLowerCase();
    if (uuidToDisplayStep && typeof uuidToDisplayStep.get === 'function' && uuidToDisplayStep.has(uuid)) {
        return `Step ${uuidToDisplayStep.get(uuid)}`;
    }
    return '';
}

export default function StepDetail({ step = {}, dynamicResult = null, uuidToDisplayStep = null }) {
    const { convertToDisplay } = useVariables();
    const fileSuggestions = useSelector((state) => state.replay?.fileSuggestions ?? []);
    const code = useMemo(() => {
        const raw = step?.executionStrategies?.length > 0 && step?.executionStrategies.find(strategy => strategy?.type === 'playwright_code')?.code;
        return replaceFileRefsWithNames(convertToDisplay(raw), fileSuggestions);
    }, [step?.executionStrategies, convertToDisplay, fileSuggestions]);
    const dynamicCode = useMemo(() => {
        const raw = step?.executionStrategies?.length > 0 && step?.executionStrategies.find(strategy => strategy?.type === 'playwright_dynamic_code')?.code;
        return replaceFileRefsWithNames(convertToDisplay(raw), fileSuggestions);
    }, [step?.executionStrategies, convertToDisplay, fileSuggestions]);
    const executionMode = step?.executionMode || EXECUTION_MODES.SMART;
    const executionModeLabel = executionMode === EXECUTION_MODES.SMART ? 'Smart Execution (AI Code + XPath Fallbacks)' : 'XPath Only (Skip AI Code)';
    const toolUsed = step?.tool;
    const argumentsObject = useMemo(() => {
        if (!step?.args) return null;
        return Object.fromEntries(
            Object.entries(step.args).map(([key, value]) => {
                const converted = convertToDisplay(value);
                const str = typeof converted === 'string' ? converted : (converted != null ? String(converted) : '');
                return [key, replaceFileRefsWithNames(str, fileSuggestions)];
            })
        );
    }, [step?.args, convertToDisplay, fileSuggestions]);
    const expectedOutcome = argumentsObject?.expectedOutcome || null;
    const assertionCode = argumentsObject?.assertionCode || null;
    const stepDescription = argumentsObject?.stepDescription || null;
    const xpathSelectors = useMemo(() => {
        return step?.executionStrategies
            ?.filter(strategy => !["playwright_dynamic_code", "playwright_code"].includes(strategy?.type) && strategy?.type !== '')
            ?.map(strategy => ({
                ...strategy,
                selector: replaceFileRefsWithNames(convertToDisplay(strategy?.selector), fileSuggestions),
            }));
    }, [step?.executionStrategies, convertToDisplay, fileSuggestions]);
    const isEvaluated = toolUsed === 'evaluateTestStep';

    const isDynamic = useMemo(
        () => isDynamicStep(step, dynamicResult),
        [dynamicResult?.dynamicStepIds, step?.stepId, step?.id],
    );

    const metaInfoModel = useMemo(() => {
        if (!isDynamic || !dynamicResult) {
            return { intentType: '', intentText: '', bindingRows: [], intentTokens: [] };
        }
        const identity = resolveDynamicStepIdentity({
            step,
            stepIndex: step?.stepIndex,
            fallbackToIndex: true,
        });
        const rawBindings = getDynamicBindingsForStep(dynamicResult?.dynamicBindings, identity);
        const dynamicIntentFromStep = step?.dynamicIntent != null ? String(step.dynamicIntent).trim() : '';
        const intentFromStep = dynamicIntentFromStep || null;
        const intentRaw = intentFromStep ?? resolveDynamicIntentRaw(
            dynamicResult,
            step?.id,
            step?.stepIndex,
            identity.candidateKeys,
        );
        const { type: intentType, intent: intentText } = parseDynamicIntentLabel(intentRaw);
        const bindingRows = buildUniqueSourceKeyBindingRows(rawBindings).map((row) => ({
            ...row,
            valueDisplay: bindingPlaceholderDisplay(row.placeholder, uuidToDisplayStep),
        }));
        return {
            intentType,
            intentText,
            bindingRows,
            intentTokens: uniqueIntentTokensFromBindings(rawBindings),
        };
    }, [isDynamic, dynamicResult, step, uuidToDisplayStep]);

    const hasStepMetaInfo = useMemo(() => {
        const m = metaInfoModel;
        return Boolean(
            (m.intentType && String(m.intentType).trim())
            || (m.intentText && String(m.intentText).trim())
            || (Array.isArray(m.bindingRows) && m.bindingRows.length > 0)
            || (Array.isArray(m.intentTokens) && m.intentTokens.length > 0)
        );
    }, [metaInfoModel]);

    return (
        <div className={styles.stepContentInner}>
            <div className={styles.stepContentInfo}>
                {code && <div className={styles.stepContentInfoItem}>
                    <span className={styles.stepContentLabel}>Execution Mode:</span>
                    <span className={styles.stepContentDescription}>{executionModeLabel}</span>
                </div>}
                <div className={styles.stepContentInfoItem}>
                    <span className={styles.stepContentLabel}>Tool Used:</span>
                    <span className={styles.stepContentDescription}>{toolUsed}</span>
                </div>
            </div>
            {code && !isEvaluated && <div>
                <span className={styles.stepContentLabel}>AI Generated Code (Primary):</span>
                <span className={styles.stepContentDescription}>{code}</span>
            </div>}
            {dynamicCode && !isEvaluated && <div>
                <span className={styles.stepContentLabel}>Dynamic AI Generated Code:</span>
                <span className={styles.stepContentDescription}>{dynamicCode}</span>
            </div>}
            {argumentsObject && Object.keys(argumentsObject).length > 0 && !isEvaluated && <div>
                <span className={styles.stepContentLabel}>Arguments:</span>
                <div>
                    {Object.entries(argumentsObject).map(([key, value]) => {
                        const valueString = typeof value === 'boolean' ? String(value) : value;
                        if(SKIP_ARGUMENTS.includes(key) || !valueString) {
                            return null;
                        }
                        return (
                            <div key={key}>
                                <span className={styles.stepContentLabel}>{key}:</span>
                                <span className={styles.stepContentDescription}>{valueString}</span>
                            </div>
                        )
                    })}
                </div>
            </div>}
            {
                xpathSelectors?.length > 0 && !isEvaluated && <div>
                    <span className={styles.stepContentLabel}>XPath Selectors:</span>
                    <div>
                        {xpathSelectors.map((selector) => (
                            <div key={selector.id}>
                                <span className={styles.stepContentLabel}>{selector.priority}.</span>
                                <span className={styles.stepContentLabel}>{LABEL_MAP[selector.type]}:</span>
                                <span className={styles.stepContentDescription}>{selector.selector}</span>
                            </div>
                        ))}
                    </div>
                </div>
            }
            {
                isEvaluated && <>
                    <div>
                        <span className={styles.stepContentLabel}>Step Description:</span>
                        <span className={styles.stepContentDescription}>{stepDescription}</span>
                    </div>
                    <div>
                        <span className={styles.stepContentLabel}>Expected Outcome:</span>
                        <span className={styles.stepContentDescription}>{expectedOutcome}</span>
                    </div>
                    <div>
                        <span className={styles.stepContentLabel}>Assertion Code:</span>
                        <span className={styles.stepContentDescription}>{assertionCode}</span>
                    </div>
                </>
            }
            {isDynamic && hasStepMetaInfo && (
                <StepMetaInfoRunInfo
                    intentType={metaInfoModel.intentType}
                    intentText={metaInfoModel.intentText}
                    bindingRows={metaInfoModel.bindingRows}
                    intentTokens={metaInfoModel.intentTokens}
                    className={styles.stepContentMetaInfo}
                />
            )}
        </div>
    );
}